import { Component, DestroyRef, OnInit, computed, effect, inject, input, output, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, ValidationErrors, Validators } from '@angular/forms'
import { PersonaModel } from '@app/v2/personas/models/persona.model'
import { PersonaService } from '@app/v2/personas/services/persona.service'
import { Persona, PersonaClonedPayload } from '@app/v2/personas/types/persona.type'
import { BasicInputComponent } from '@app/v2/shared/components/inputs/basic-input/basic-input.component'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { ProgressCircleComponent } from '@app/v2/shared/components/progress-circle/progress-circle.component'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { AuthService } from '@auth/services/auth.service'
import { environment } from '@environments/environment'
import { finalize } from 'rxjs'

@Component({
  selector: 'clone-persona-modal',
  standalone: true,
  imports: [ModalComponent, BasicInputComponent, ProgressCircleComponent],
  templateUrl: './clone-persona-modal.component.html'
})
export class ClonePersonaModalComponent implements OnInit {
  public readonly id = input('clone-persona-modal')
  public readonly cloned = output<PersonaClonedPayload>()
  public readonly onCancel = output<void>()

  public readonly workspaceId = input.required<number>()
  public readonly eventId = input.required<number>()
  public readonly sourcePersona = input.required<Persona>()
  public readonly eventSlug = input('')
  public readonly isSubmitting = signal(false)
  public personaName = new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(255)] })
  public personaSlug = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(3), Validators.maxLength(100)]
  })
  public readonly registrationUrl = computed(() => {
    const organizationSlug = this._auth.currentWorkspace?.slug ?? ''
    const eventSlug = (this.eventSlug() || '').trim()
    return `${environment.landingPageUrl}workspaces/${organizationSlug}/events/${eventSlug}/forms/${this._slugPreview()}`
  })

  public readonly cloneIcon = `
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.1911 0.00000766824H9.20038C8.90012 -0.000782141 8.6027 0.0594466 8.32531 0.177207C8.04793 0.294968 7.79611 0.467922 7.58442 0.686056L7.04576 1.2363C6.9613 1.32795 6.90009 1.43944 6.86749 1.56098H10.1436C10.945 1.56861 11.7106 1.90162 12.2725 2.48692C12.8344 3.07223 13.1467 3.86201 13.1408 4.68294V12.4878H13.1911C13.5885 12.4805 13.9666 12.312 14.2428 12.0193C14.5191 11.7267 14.6707 11.3338 14.6646 10.9268V1.56098C14.6707 1.154 14.5191 0.761102 14.2428 0.468486C13.9666 0.17587 13.5885 0.0073985 13.1911 0.00000766824Z" fill="#111928"/>
    <path d="M5.52199 7.02438V3.12194C5.52299 3.00819 5.54904 2.89615 5.59817 2.79414C5.09406 2.9019 4.63168 3.15826 4.26792 3.53169L2.11255 5.73969C1.76902 6.09391 1.52775 6.5385 1.41542 7.02438H5.52199Z" fill="#111928"/>
    <path d="M10.1436 2.7317H6.92919C7.00194 2.84853 7.04224 2.98344 7.04576 3.12195V7.02439C7.04576 7.43839 6.88522 7.83543 6.59946 8.12817C6.31369 8.4209 5.92611 8.58537 5.52199 8.58537H1.33161V14.0488C1.32551 14.5592 1.51722 15.0512 1.86471 15.4169C2.21219 15.7827 2.6871 15.9923 3.18528 16H10.1436C10.6418 15.9923 11.1168 15.7827 11.4644 15.417C11.812 15.0512 12.0038 14.5592 11.998 14.0488V4.68293C12.0038 4.17249 11.812 3.68048 11.4644 3.31472C11.1168 2.94897 10.6418 2.73931 10.1436 2.7317Z" fill="#111928"/>
  </svg>
  `

  private readonly _auth = inject(AuthService)
  private readonly _personaService = inject(PersonaService)
  private readonly _alertService = inject(AlertService)
  private readonly _slugPreview = signal('')
  private readonly _destroyRef = inject(DestroyRef)

  constructor() {
    effect(() => {
      const persona = this.sourcePersona()
      const defaults = PersonaModel.buildCloneDefaults(persona.name, persona.slug ?? '')
      this.personaName.setValue(defaults.name, { emitEvent: false })
      this.personaSlug.setValue(defaults.slug, { emitEvent: false })
      this._slugPreview.set(defaults.slug)
    })
  }

  public ngOnInit(): void {
    this.personaName.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => this._clearServerError(this.personaName))
    this.personaSlug.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((value) => {
      const sanitized = PersonaModel.sanitizeSlug(value)
      if (sanitized !== value) this.personaSlug.setValue(sanitized, { emitEvent: false })
      this._slugPreview.set(sanitized)
      this._clearServerError(this.personaSlug)
    })
  }

  private _applyApiErrors(body: unknown): void {
    this._setServerError(this.personaName, PersonaModel.validationErrorsFromFieldMessages(body, 'name'))
    this._setServerError(this.personaSlug, PersonaModel.validationErrorsFromFieldMessages(body, 'slug'))
  }

  private _setServerError(control: FormControl<string>, errors: ValidationErrors | null): void {
    if (!errors) {
      this._clearServerError(control)
      return
    }
    control.setErrors({ ...control.errors, ...errors })
    control.markAsTouched()
  }

  private _clearServerError(control: FormControl<string>): void {
    if (!control.hasError('serverError')) return
    control.updateValueAndValidity({ emitEvent: false })
  }

  /**
   * Submits when the user presses Enter in either field.
   */
  public onFieldKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') return
    event.preventDefault()
    this.onConfirm()
  }

  /**
   * Clones the source persona when both fields are valid.
   */
  public onConfirm(): void {
    if (this.isSubmitting()) return
    const name = this.personaName.value.trim()
    const slug = PersonaModel.sanitizeSlug(this.personaSlug.value)
    this.personaName.setValue(name, { emitEvent: false })
    this.personaSlug.setValue(slug, { emitEvent: false })
    this._slugPreview.set(slug)
    this.personaName.markAsTouched()
    this.personaSlug.markAsTouched()
    if (this.personaName.invalid || this.personaSlug.invalid) return

    const source = this.sourcePersona()
    this.isSubmitting.set(true)
    this._personaService
      .clonePersona(this.workspaceId(), this.eventId(), source.id, { name, slug })
      .pipe(
        finalize(() => this.isSubmitting.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (res) => this.cloned.emit({ persona: res.data, message: res.message }),
        error: (err) =>
          PersonaModel.settleClonePersonaError(
            err?.error ?? null,
            (body) => this._applyApiErrors(body),
            (message) => this._alertService.alert(Alert.ERROR, message)
          )
      })
  }

  /**
   * Handles modal close / cancel.
   */
  public handleModalClose(): void {
    if (this.isSubmitting()) return
    this.onCancel.emit()
  }
}
